import React from 'react';
import { Link } from 'react-router-dom';
import { Gamepad2, Shield, Cpu, Cloud } from 'lucide-react';
import { TwitterIcon, GithubIcon, LinkedinIcon, YoutubeIcon } from './SocialIcons';

export const Footer: React.FC = () => {
  return (
    <footer className="bg-slate-950 border-t border-slate-800/80 text-slate-400 text-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          {/* Col 1: Studio info */}
          <div className="md:col-span-1 space-y-4">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-cyan-500 to-fuchsia-500 p-0.5">
                <div className="w-full h-full bg-slate-950 rounded-[6px] flex items-center justify-center">
                  <span className="font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-fuchsia-400 text-sm">
                    GSR
                  </span>
                </div>
              </div>
              <span className="text-lg font-bold text-white tracking-wide">
                GSR <span className="text-cyan-400">STUDIO</span>
              </span>
            </Link>
            <p className="text-slate-400 text-xs leading-relaxed">
              Crafting immersive, physics-driven worlds and next-gen gaming experiences across edge networks and next-gen hardware.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noreferrer"
                className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 transition"
              >
                <TwitterIcon className="w-4 h-4" />
              </a>
              <a
                href="https://github.com"
                target="_blank"
                rel="noreferrer"
                className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:border-slate-700 transition"
              >
                <GithubIcon className="w-4 h-4" />
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noreferrer"
                className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-red-400 hover:border-red-500/50 transition"
              >
                <YoutubeIcon className="w-4 h-4" />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noreferrer"
                className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-blue-400 hover:border-blue-500/50 transition"
              >
                <LinkedinIcon className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Col 2: Navigation */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Explore</h4>
            <ul className="space-y-2.5 text-xs">
              <li>
                <Link to="/games" className="hover:text-cyan-400 transition">
                  Game Titles
                </Link>
              </li>
              <li>
                <Link to="/staff" className="hover:text-cyan-400 transition">
                  Core Team & Staff
                </Link>
              </li>
              <li>
                <Link to="/faq" className="hover:text-cyan-400 transition">
                  Studio FAQ
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-cyan-400 transition">
                  Contact & Press Kit
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3: Legal & Policy */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Governance</h4>
            <ul className="space-y-2.5 text-xs">
              <li>
                <Link to="/terms" className="hover:text-cyan-400 transition">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-cyan-400 transition">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/admin" className="hover:text-cyan-400 transition flex items-center gap-1.5">
                  <Shield className="w-3 h-3 text-cyan-400" />
                  <span>Admin Portal</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4: Tech Stack info */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Architecture</h4>
            <div className="bg-slate-900/60 rounded-xl p-4 border border-slate-800/80 space-y-2.5 text-xs">
              <div className="flex items-center gap-2 text-slate-300">
                <Cloud className="w-4 h-4 text-orange-400" />
                <span>Cloudflare Pages & Workers</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Cpu className="w-4 h-4 text-cyan-400" />
                <span>Cloudflare D1 SQL Database</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Gamepad2 className="w-4 h-4 text-purple-400" />
                <span>Three.js WebGL 3D Engine</span>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} GSR STUDIO. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="hover:text-slate-400">Terms</Link>
            <Link to="/privacy" className="hover:text-slate-400">Privacy</Link>
            <Link to="/contact" className="hover:text-slate-400">Support</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
